import { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import FooterToggle from './FooterToggle';
import { FONT_FAMILY } from '../../../constants/typography';

// Warning orange (matches the footer's `warning` status tone) used to flag that
// the app is in Test mode — i.e. the "Live" toggle is off and no events are
// being pushed to RabbitMQ.
const TEST_MODE_ORANGE = '#F4A336';

const FooterTogglesMenu = ({ toggles = [], selectors = [], compactStatus = false }) => {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  // Test mode is active when the "Live" toggle is explicitly off.
  const isTestMode = toggles.some((t) => t.label === 'Live' && t.value === false);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          cursor: 'pointer',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: isTestMode
            ? TEST_MODE_ORANGE
            : (open ? 'rgba(254,236,103,0.4)' : 'rgba(255,255,255,0.12)'),
          color: isTestMode
            ? TEST_MODE_ORANGE
            : (open ? '#FEEC67' : 'rgba(255,255,255,0.7)'),
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: isTestMode ? TEST_MODE_ORANGE : 'rgba(254,236,103,0.4)',
            color: isTestMode ? TEST_MODE_ORANGE : '#FEEC67',
          },
        }}
      >
        <TuneRoundedIcon sx={{ fontSize: compactStatus ? 19 : 15 }} />
        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: compactStatus ? 16 : 13, fontWeight: 500, letterSpacing: '0.015em' }}>
          Settings
        </Typography>
        <IconButton size="small" disableRipple sx={{ p: 0, color: 'inherit' }}>
          {open
            ? <KeyboardArrowDownRoundedIcon sx={{ fontSize: compactStatus ? 20 : 16 }} />
            : <KeyboardArrowUpRoundedIcon sx={{ fontSize: compactStatus ? 20 : 16 }} />
          }
        </IconButton>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: '#2A2A2A',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
              minWidth: 160,
              pb: 0.5,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Settings
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        {toggles.length > 0 && (
          <Stack sx={{ px: 1.5, pt: 1, pb: 0.5 }} spacing={0.25}>
            {toggles.map((toggle) => (
              <Stack
                key={toggle.label}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>
                  {toggle.label}
                </Typography>
                <FooterToggle {...toggle} hideLabel />
              </Stack>
            ))}
          </Stack>
        )}

        {selectors.map((selector) => (
          <Box key={selector.label}>
            {toggles.length > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 0.5 }} />}
            <Box sx={{ px: 2, pt: 0.75 }}>
              <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {selector.label}
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                pt: 0.75,
                pb: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
              }}
            >
              {(selector.options || []).map((option) => {
                const active = option.id === selector.value;
                return (
                  <Box
                    key={option.id}
                    onClick={() => selector.onChange?.(option.id)}
                    sx={{
                      cursor: 'pointer',
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: active ? 'rgba(254,236,103,0.5)' : 'rgba(255,255,255,0.12)',
                      bgcolor: active ? 'rgba(254,236,103,0.12)' : 'transparent',
                      color: active ? '#FEEC67' : 'rgba(255,255,255,0.7)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: 'rgba(254,236,103,0.4)',
                        color: '#FEEC67',
                      },
                    }}
                  >
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}

      </Popover>
    </>
  );
};

FooterTogglesMenu.propTypes = {
  toggles: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.bool,
    onChange: PropTypes.func,
    tooltip: PropTypes.string,
    // In use since the NLE toggle shipped, but never declared — an unknown key
    // in a shape is permitted, so the omission hid an in-use prop from readers.
    disabled: PropTypes.bool,
  })),
  selectors: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })),
    onChange: PropTypes.func,
  })),
  compactStatus: PropTypes.bool,
};

export default FooterTogglesMenu;
